import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as Rx from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { EUILanguage, AppSettingService, AppUtilityService } from '../app.service';
import { UserInfoService } from '../user/user-info.service';

@Injectable({
	providedIn: 'root'
})
export class InterestSurveyAPIService {
	ScoreChanged: any = null;

	constructor(
		private userInfo: UserInfoService,
		private http: HttpClient
	) {
		this.ScoreChanged = new Rx.BehaviorSubject(1);
	}

	private url = AppSettingService.appConfig.JourneyAPIUrl + "InterestSurvey";
	private scoreCache: any = null;
	public saveObject: any = null;
	public riasecArray: any = null;

	getMyLastResult(): Rx.Observable<any> {
		if (this.scoreCache != null) {
			return Rx.of(this.scoreCache);
		}
		else {
			return this.http.get<any>(
				this.url + '/Result?isAll=false',
				{
					withCredentials: true,
					headers: { 'Authorization': this.userInfo.authHeader(false) }
				}
			).pipe(
				tap(
					response => {
						this.scoreCache = response;
					}
				)
			);
		}
	}

	getStudentResult(userId: string, isAll: boolean): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + '/Result?UserId=' + encodeURIComponent(userId) + '&isAll=' + encodeURIComponent(isAll),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		).pipe(
			map(
				response => {
					response.forEach((survey: any) => {
						//Remove the -XX:XX part of the return to show the local time.
						survey.DateTaken = survey.DateTaken.substring(0, survey.DateTaken.length - 6);
						survey.DateTaken = survey.DateTaken.replace("T", " | ");
						if (survey.ManualEntry == true) {
							survey.DateTaken = survey.DateTaken + " - Manual";
						}
						survey.Scores = [survey.Realistic, survey.Investigative, survey.Artistic, survey.Social, survey.Enterprising, survey.Conventional];
					});
					return response;
				}
			)
		);
	}

	// if queueFailure, server may return 202 to indicate recommended soc is not available
	saveRIASECScore(surveyObject: any): Rx.Observable<any> {
		let queueFailure: boolean = true;
		if (this.userInfo.isAnonymous()) {
			queueFailure = false;
		}
		return this.http.post<any>(
			this.url + "/Result?institutionCode=" + encodeURIComponent(this.userInfo.getInstitutionCode()) + "&queueFailure=" + queueFailure.toString() + "&userEmail=" + encodeURIComponent(this.userInfo.anonymousEmail),
			surveyObject,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		).pipe(tap(() => {
			// update score change event
			this.ScoreChanged.next(this.ScoreChanged.getValue() + 1);
			// remove myresult cache
			this.scoreCache = null;
		}));
	}

	getSurveyResultOccupation(): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Result/Occupation",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		).pipe(map(response => {
			if (response.RecommendedOccupation != null) {
				return response.RecommendedOccupation;
			}
			else {
				throw new Error("No recommended interest occupations are found.");
			}
		}));
	}

	shareSurveResult(result: any, careerVersion: string): Rx.Observable<any> {
		return this.http.post<any>(
			this.url + '/Result/Share?careerVersion=' + encodeURIComponent(careerVersion),
			result,
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	getSurveyQuestion(language: string): Rx.Observable<any> {
		return this.http.get<any>(
			this.url + "/Survey/" + language + "?isShort=false",
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) }
			}
		);
	}

	downloadSurveResult(careerVersion: string): Rx.Observable<Blob> {
		return this.http.get<any>(
			this.url + '/Result/Share/pdf?careerVersion=' + encodeURIComponent(careerVersion),
			{
				withCredentials: true,
				headers: { 'Authorization': this.userInfo.authHeader(false) },
				responseType: 'blob' as 'json'
			}
		);
	}
}


@Injectable({
	providedIn: 'root'
})
export class InterestSurveyResultService {
	private xRIASECObjs = [
		{
			"label": "Realistic",
			"iconFont": "icnScaleBalanced",
			"description": "Realistic personality types are independent, stable, persistent, genuine, practical, and thrifty.",
			"detailedDescription": "People who are independent, stable, persistent, genuine, practical, and thrifty. They tend to be no-nonsense and down-to-earth while enjoying physical, athletic, or mechanical activity. They prefer things rather than ideas or people such as being outdoors, using tools, operating machines, interacting with animals, and working with their hands. They also value the natural, concrete, and tangible.",
			"editTip": "You see things as they are and rely most heavily on your sense of sight and touch.",
			"i": 0
		},
		{
			"label": "Investigative",
			"iconFont": "icnEye",
			"description": "Investigative personality types are intellectual, introspective, inquisitive, curious, methodical, rational, analytical, and logical.",
			"detailedDescription": "People who are intellectual, introspective, inquisitive, curious, methodical, rational, analytical, and logical. They prefer tasks that are scholarly, scientific, technical, or medical and activities that involve thought, observation, investigation, exploration, and discovery. They like to solve problems, perform experiments, and conduct research.",
			"editTip": "You’re a super sleuth who loves to solve math and science problems.",
			"i": 1
		},
		{
			"label": "Artistic",
			"iconFont": "icnPalette",
			"description": "Artistic personality types are creative, intuitive, sensitive, articulate, expressive, unstructured, original, nonconforming, and innovative.",
			"detailedDescription": "People who are creative, intuitive, sensitive, articulate, expressive, unstructured, original, nonconforming, and innovative who rely on feelings, imagination, inspiration and who are spontaneous and open-minded. They prefer to work with ideas, abstractions, and concepts. They also enjoy work that is literary, verbal, visual and aesthetic. They typically excel in art, music, dance, drawing, painting, sculpting, drafting, writing, drama, communicating, design, fashion.",
			"editTip": "Your electives are full of art, drama, creative writing, and music classes.",
			"i": 2
		},
		{
			"label": "Social",
			"iconFont": "icnUserGroup",
			"description": "Social personality types are kind, generous, cooperative, patient, caring, helpful, empathetic, tactful, and friendly.",
			"detailedDescription": "People who are kind, generous, cooperative, patient, caring, helpful, empathetic, tactful, and friendly. They prefer tasks that involve socializing, helping others, teaching, teamwork, social interaction, relationship building, humanitarian, educational, philanthropic, interpersonal, and service-oriented work.",
			"editTip": "You’re a friend to everyone. You like to help, interact and share information.",
			"i": 3
		},
		{
			"label": "Enterprising",
			"iconFont": "icnEarthAmericas",
			"description": "Enterprising personality types are adventurous, ambitious, assertive, extroverted, energetic, enthusiastic, confident, optimistic, dominant, persuasive, and motivational.",
			"detailedDescription": "People who are adventurous, ambitious, assertive, extroverted, energetic, enthusiastic, confident, optimistic, dominant, persuasive, and motivational. They prefer work that involves leadership, business, politics, and public speaking. They enjoy being in charge, taking risks, debating and competing.",
			"editTip": "You’re persuasive and could sell just about anything to anyone.",
			"i": 4
		},
		{
			"label": "Conventional",
			"iconFont": "icnPenRuler",
			"description": "Conventional personality types are conscientious and conservative, logical, efficient, orderly, organized, thorough, and detail-oriented.",
			"detailedDescription": "People who are conscientious and conservative, logical, efficient, orderly, organized, thorough, and detail-oriented. They are individuals who value precision and accuracy. They excel in practical tasks, quantitative measurements, structured environments and people who follow the rules. They prefer work that involves accounting, statistics, mathematics, numerical activities, and office settings.",
			"editTip": "People might call you TYPE A, but they’re just jealous of your ability to get things done.",
			"i": 5
		}
	];
	private results: any = null;

	private _interestSurveyAnonymousTaken: any = null;
	get anonymousTaken(): any {
		return this._interestSurveyAnonymousTaken;
	}
	set anonymousTaken(v: any) {
		if (v == null) {
			this._interestSurveyAnonymousTaken = null;
		}
		else {
			// when transfer anonymous skill assessment during registration, we only save the last skill assessment 
			// while need to note down which progresses (skill, interest, career explorer) users do first
			// so do not change AnswerTime if answer before
			if (this.userInfo.isAnonymous()) {
				let old = this._interestSurveyAnonymousTaken;
				if (old != null) {
					v.TakenTime = old.TakenTime;
				}
			}
			this._interestSurveyAnonymousTaken = v;
			if (this.anonymousTakenTime == null) {
				this.anonymousTakenTime = v.TakenTime;
				this.anonymousTakenTimeChanged.next(true);
			}
		}
	}
	// anonymousTakenTime: when user enter score manually (local popup), update this value to have UI fresh the my progress properly
	anonymousTakenTime:any = null;
	anonymousTakenTimeChanged: Rx.BehaviorSubject<boolean> = new Rx.BehaviorSubject<boolean>(false);

	private _interestSurveyScore: any = null;
	get interestSurveyScore(): any {
		return this._interestSurveyScore;
	}
	set interestSurveyScore(v: any) {
		if (v == null) {
			this._interestSurveyScore = null;
		}
		else {
			this._interestSurveyScore = v;
		}
	}

	private _interestSurveyRecommendedOccupations: any = null;
	get interestSurveyRecommendedOccupations(): any {
		return this._interestSurveyRecommendedOccupations;
	}
	set interestSurveyRecommendedOccupations(v: any) {
		if (v == null) {
			this._interestSurveyRecommendedOccupations = null;
		}
		else {
			this._interestSurveyRecommendedOccupations = v;
		}
	}

	getSurveyResultOccupation(): void {
		this.interestAPI.getSurveyResultOccupation().subscribe({
			next: (response: any) => {
				this.interestSurveyRecommendedOccupations = response;
			},
			error: (err: any) => {
				this.interestSurveyRecommendedOccupations = null;
				console.log(err);
			}
		});
	}

	getInterestFit(onetCode: string): number {
		if (this.interestSurveyRecommendedOccupations == null) {
			return 0;
		}
		let match = this.interestSurveyRecommendedOccupations.find((x: any) => x.old_onet_code === onetCode);
		if (match !== undefined && match != null) {
			return match.fit_score;
		}
		else {
			return 0;
		}
	}

	storeResults(riasec: number[], manualEntry: boolean, surveyId: string) {
		manualEntry = manualEntry == null ? false : manualEntry;
		surveyId = surveyId == null ? '' : surveyId.trim();
		surveyId = surveyId === '' ? null : surveyId;
		this.anonymousTaken = { SurveyId: surveyId, ManualEntry: manualEntry, Score: riasec, TakenTime: new Date() };
		this.results = riasec;
		this.interestSurveyScore = { "score": riasec };
		// the score watch will refresh the result for user
		if (this.userInfo.isAnonymous()) {
			this.getSurveyResultOccupation();
		}
	}

	getResults(): any {
		if (this.results != null) {
			return this.results;
		}
		else {
			var survey = this.interestSurveyScore;
			if (survey != null) {
				return survey.score;
			}
			else {
				return null;
			}
		}
	}

	getAnonymousRIASEC(): any {
		var survey = this.interestSurveyScore;
		if (survey != null && survey.score != null && survey.score.length > 0) {
			return survey.score;
		}
		else {
			return null;
		}
	}

	// get the last interest survey scores
	getLastRIASEC(): Rx.Observable<any> {
		if (this.userInfo.isAnonymous()) {
			let score = this.getResults();
			if (score != null) {
				return Rx.of(
					{
						"HasRecommendedSOC": true,
						"ScoreList": score[0] + "," + score[1] + "," + score[2] + "," + score[3] + "," + score[4] + "," + score[5]
					}
				);
			}
			else {
				return Rx.throwError(() => { "no score" });
			}
		}
		else {
			return this.interestAPI.getMyLastResult().pipe(map(
				response => {
					if (response.length > 0) {
						var scoreList = response[0].Realistic + "," + response[0].Investigative + "," + response[0].Artistic + "," + response[0].Social + "," + response[0].Enterprising + "," + response[0].Conventional;
						return {
							"HasRecommendedSOC": response[0].HasRecommendedSOC,
							"ScoreList": scoreList
						};
					}
					else {
						throw new Error("no score"); //inside pipe cannot use throwerror, it will not fire
					}
				}
			));
		}
	}

	calcPercent(val: number): number {
		return parseInt((val / 40 * 100).toString());
	}

	calcMaxScores(list: number[]): number[] {
		if (list == null) {
			return [];
		}
		let listIndex = [] as any[];
		list.forEach((val: any, i: any) => {
			var listItem = {
				"val": val,
				"i": i
			};
			listIndex.push(listItem);
		});

		listIndex.sort((a: any, b: any) => {
			a = a.val;
			b = b.val;
			return b - a;
		});

		return [listIndex[0].i, listIndex[1].i, listIndex[2].i];
	}

	createRIASECObjs(list: number[]):any {
		if (list == null) {
			return [];
		}
		// Prior assign the value and percent, need to re-order RIASECObjs first. The sequence must be RIASEC R first, C last
		//the order may get messed up because it is drectly passed to caller.
		this.xRIASECObjs = this.xRIASECObjs.sort((a: any, b: any) => {
			return parseInt(a.i) - parseInt(b.i);
		});
		list.forEach((val, i) => {
			(this.xRIASECObjs[i] as any)["percent"] = this.calcPercent(val);
			(this.xRIASECObjs[i] as any)["value"] = val;
			(this.xRIASECObjs[i] as any)["oldValue"] = val;
		});
		return AppUtilityService.copyObject(this.xRIASECObjs);
	}

	private questionLanguage: EUILanguage = EUILanguage.English;

	getQuestionLanguage(): EUILanguage {
		return this.questionLanguage;
	}

	updateQuestionLanguage(lang: EUILanguage) {
		this.questionLanguage = lang;
	}

	// watch interest survery change, not need to monitor because it is not in same page
	private scoreMonitor: Rx.Subscription = null;
	ngOnDestroy() {
		if (this.scoreMonitor != null) {
			this.scoreMonitor.unsubscribe();
		}
	}

	constructor(
		private userInfo: UserInfoService,
		private interestAPI: InterestSurveyAPIService
	) {
		if (!this.userInfo.isAnonymous()) {
			this.scoreMonitor = this.interestAPI.ScoreChanged.subscribe(
				() => {
					this.getSurveyResultOccupation();
				},
				(error: any) => {
					console.log(error);
				}
			);
		}
	}
}
